import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import css from './index.module.css';

import SEO from "@components/seo";
import MainContent from "@components/content";
import ContainerMini from "@components/containerMini";
import * as Types from '../../../types';
import useUserData from "@hooks/useUserData";

export default function AdmTransactions() {
  const getYearMonth = (date: any) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  };

  const [startDate, setStartDate] = useState<any>(getYearMonth(new Date()));
  const [endDate, setEndDate] = useState<any>(getYearMonth(new Date()));

  const [transactions, setTransactions] = useState<any>({});
  const [selected, setSelected] = useState<any>({});
  const navigate = useNavigate();
      
  useEffect(() => {
    // Вычисляем первую дату выбранного начального месяца
    const firstDay = new Date(startDate.split('-')[0], startDate.split('-')[1] - 1, 1)
      .toISOString()
      .split('T')[0];

    // Вычисляем последнюю дату выбранного конечного месяца
    const endMonthDate = new Date(endDate.split('-')[0], endDate.split('-')[1] - 1, 1);
    const lastDay = new Date(endMonthDate.getFullYear(), endMonthDate.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];
    
    // Проверка, чтобы начальная дата не была позже конечной
    if (new Date(firstDay) > new Date(lastDay)) {
        console.error("Дата начала не может быть позже даты окончания.");
        setTransactions({}); // Очищаем данные или показываем ошибку
        return;
    }

    // Выполняем запрос к API
    axios.get(`${import.meta.env.VITE_API_ADM_URL}/pay/transactions?startDate=${firstDay}&endDate=${lastDay}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('authToken')
        }
      })
      .then(response  => {
        setTransactions(response.data);
      })
      .catch(error => {
        setTransactions({});
      });
  }, [startDate, endDate]);

  const formatNumber = (num: any) => {
    if (num == null) return '0';
    return num.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const calculateNetIncome = (number: number, tariff: number) => {
    let netIncome = number;
    netIncome -= (netIncome * 4) / 100;
    netIncome -= (netIncome * 8) / 100;

    switch (tariff) {
      case 0:
        netIncome -= 850;
        break;
      case 1:
        netIncome -= 1650;
        break;
      case 2:
        netIncome -= 4200;
        break;
      default:
        console.error('Недопустимый тариф');
    }
    return netIncome;
  };

  return (
    <MainContent>    
        <SEO 
          title="Расходы"
          description=""
          ogUrl="https://artcons.io/"
        />

        <div className={css.head}>
          <h1>Расходы</h1>
          <div className={css.dateContainer}>
            <label htmlFor="start-date" style={{fontWeight: 'bold'}}>С:</label>
            <input 
                id="start-date"
                type="month"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={css.input}
            />
            <label htmlFor="end-date" style={{fontWeight: 'bold'}}>По:</label>
            <input 
                id="end-date"
                type="month"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={css.input}
            />
          </div>
        </div>

        <h3>Акт сверки</h3>

        <div className={css.act}>
          <div className={css.row} style={{ borderTop: '1px solid rgba(0, 0, 0, 0.301)'}}>
            <b>Дата начала периода</b>
            <a>{formatDate(transactions?.startDate)}, 00:00</a>
          </div>

          <div className={css.row}>
            <b>Дата конца периода</b>
            <a>{formatDate(transactions?.endDate)}, 23:59</a>
          </div>

          <div className={css.row}>
            <b>Всего получено</b>
            <a>{formatNumber(transactions?.totalWithoutTax)} ₽</a>
          </div>

          <div className={css.row}>
            <b>Всего получено с вычетом комиссии эквайринга</b>
            <a>{formatNumber(transactions?.totalAfterCommission)} ₽</a>
          </div>

          <div className={css.row}>
            <b>Всего получено с вычетом налога (8%)</b>
            <a>{formatNumber(transactions?.totalWithTax)} ₽</a>
          </div>

          <div className={css.row}>
            <b>Всего получено с вычетом расходов (серверы)</b>
            <a>{formatNumber(transactions?.totalAfterTariff)} ₽</a>
          </div>

          <div className={css.row} style={{ background: "#e3e3e3" }}>
            <b>Чистая прибыль за период</b>
            <b>{formatNumber(transactions?.totalAfterTariff)} ₽</b>
          </div>
        </div>

        <h3 style={{ marginTop: 30, marginBottom: 10 }}>История транзакций</h3>

        <div className={css.table}>
            <div className={css.tableHeader}>
              <a>Дата</a>
              <a>Назване</a>
              <a>Сумма</a>
              <a>Чистыми</a>
              <a>Тариф</a>
            </div>

            <div className={css.tableBody}>
            {transactions?.transactions?.map((item: any) => (
              <div className={css.tableDiv} onClick={() => navigate(`/admin/user/${item.userId}`)}>
                <a>{formatDate(item.createdAt)}</a>
                <a>{item.event}</a>
                <a>{formatNumber(item.value)} ₽</a>
                <a>{formatNumber(calculateNetIncome(item.value, item.tariff))} ₽</a>
                {item.tariff == 0 && <a>Стандарт</a>}
                {item.tariff == 1 && <a>Премиум</a>}
                {item.tariff == 2 && <a>Бизнес</a>}
              </div>
            ))}
            </div>
        </div>
    </MainContent>
  )
}